import type { TripSegment } from '../types'

interface Props {
	segment: TripSegment | null
	onClose: () => void
}

export function ShipmentDetailPanel({ segment, onClose }: Props) {
	if (!segment) return null

	const formatDateTime = (dateStr?: string) => {
		if (!dateStr) return '—'
		const date = new Date(dateStr)
		return date.toLocaleString('en-US', {
			weekday: 'short',
			month: 'short',
			day: 'numeric',
			year: 'numeric',
			hour: 'numeric',
			minute: '2-digit',
			hour12: true,
		})
	}

	const formatTrailer = (trailer: string | undefined) => {
		if (!trailer) return '—'
		// Strip trailing .0 from numeric-looking values (e.g., "1423424.0" -> "1423424")
		return trailer.replace(/\.0$/, '')
	}

	const getStatusInfo = () => {
		let label = segment.shipment_status || segment.order_status || 'Unknown'
		let bgColor = '#e5e7eb'
		let textColor = '#374151'

		switch (segment.order_status) {
			case 'D':
				label = 'Delivered'
				bgColor = '#dcfce7'
				textColor = '#166534'
				break
			case 'P':
				label = 'Picked Up'
				bgColor = '#dbeafe'
				textColor = '#1e40af'
				break
			case 'A':
				label = 'At Shipper'
				bgColor = '#fef3c7'
				textColor = '#92400e'
				break
			case 'V':
				label = 'Void'
				bgColor = '#fecaca'
				textColor = '#991b1b'
				break
			default:
				if (segment.shipment_status === 'Delivered') {
					bgColor = '#dcfce7'
					textColor = '#166534'
				}
		}

		return { label, bgColor, textColor }
	}

	const status = getStatusInfo()

	// Get actual arrival time (find the latest delivery from stops)
	const actualArrival =
		segment.meta?.actual_arrival ||
		segment.meta?.stops?.find((s) => s.actual_arrival)?.actual_arrival

	// Find scheduled arrival (from consignee_schedule_time or last stop)
	const scheduledArrival =
		segment.consignee_schedule_time ||
		segment.meta?.stops?.find((s) => s.scheduled_delivery)?.scheduled_delivery

	return (
		<div style={styles.panel}>
			{/* Header - Fixed */}
			<div style={styles.header}>
				<div style={styles.headerLeft}>
					<span style={styles.proNumber}>#{segment.pro_num}</span>
					<span
						style={{
							...styles.statusBadge,
							backgroundColor: status.bgColor,
							color: status.textColor,
						}}
					>
						{status.label}
					</span>
				</div>
				<button onClick={onClose} style={styles.closeButton}>
					✕
				</button>
			</div>

			{/* Scrollable Content */}
			<div style={styles.scrollableContent}>
				{/* Arrival Info */}
				{actualArrival && (
					<div style={styles.section}>
						<div style={styles.sectionLabel}>Actual Arrival</div>
						<div style={styles.arrivalTime}>{formatDateTime(actualArrival)}</div>
					</div>
				)}

				{scheduledArrival && (
					<div style={styles.section}>
						<div style={styles.sectionLabel}>Scheduled Arrival</div>
						<div style={styles.sectionValue}>{formatDateTime(scheduledArrival)}</div>
					</div>
				)}

				{/* Shipment Details */}
				<div style={styles.detailsGrid}>
					<div style={styles.detailItem}>
						<div style={styles.detailLabel}>Pro Number</div>
						<div style={styles.detailValue}>{segment.pro_num}</div>
					</div>

					<div style={styles.detailItem}>
						<div style={styles.detailLabel}>Customer</div>
						<div style={styles.detailValue}>{segment.customer_name || '—'}</div>
					</div>

					<div style={styles.detailItem}>
						<div style={styles.detailLabel}>BOL Number</div>
						<div style={styles.detailValue}>{segment.meta?.bol_number || '—'}</div>
					</div>

					<div style={styles.detailItem}>
						<div style={styles.detailLabel}>Shipper</div>
						<div style={styles.detailValue}>{segment.meta?.shipper || '—'}</div>
					</div>

					<div style={styles.detailItem}>
						<div style={styles.detailLabel}>Consignee</div>
						<div style={styles.detailValue}>{segment.meta?.consignee || '—'}</div>
					</div>

					<div style={styles.detailItem}>
						<div style={styles.detailLabel}>Trailer</div>
						<div style={styles.detailValue}>{formatTrailer(segment.meta?.trailer)}</div>
					</div>

					<div style={styles.detailItem}>
						<div style={styles.detailLabel}>Last updated at</div>
						<div style={styles.detailValue}>{formatDateTime(segment.last_ts)}</div>
					</div>
				</div>

				{/* Stops */}
				{segment.meta?.stops && segment.meta.stops.length > 0 && (
					<div style={styles.stopsSection}>
						<div style={styles.stopsHeader}>Stops</div>
						{segment.meta.stops
							.sort((a, b) => a.sequence - b.sequence)
							.map((stop, idx) => {
								const stopStatus =
									stop.shipment_status || (stop.delivered ? 'Delivered' : 'Pending')
								const isDelivered = stop.delivered || stopStatus === 'Delivered'

								return (
									<div key={idx} style={styles.stopCard}>
										<div style={styles.stopHeader}>
											<div style={styles.stopSequence}>
												<span style={styles.sequenceLabel}>Stop:</span>
												<span style={styles.sequenceValue}>{stop.sequence}</span>
											</div>
											<span
												style={{
													...styles.stopStatus,
													backgroundColor: isDelivered ? '#dcfce7' : '#dbeafe',
													color: isDelivered ? '#166534' : '#1e40af',
												}}
											>
												{stopStatus}
											</span>
										</div>

										{stop.scheduled_pickup && (
											<div style={styles.stopDetail}>
												<span style={styles.stopLabel}>Scheduled PU:</span>
												<span style={styles.stopValue}>
													{formatDateTime(stop.scheduled_pickup)}
												</span>
											</div>
										)}

										<div style={styles.stopDetail}>
											<span style={styles.stopLabel}>Shipper:</span>
											<span style={styles.stopValue}>
												{stop.shipper_name
													? `${stop.shipper_name} - ${stop.shipper_city || ''}`
													: '—'}
											</span>
										</div>

										<div style={styles.stopDetail}>
											<span style={styles.stopLabel}>Consignee:</span>
											<span style={styles.stopValue}>
												{stop.consignee_name
													? `${stop.consignee_name} - ${stop.consignee_city || ''}`
													: '—'}
											</span>
										</div>

										{stop.scheduled_delivery && (
											<div style={styles.stopDetail}>
												<span style={styles.stopLabel}>Scheduled:</span>
												<span style={styles.stopValue}>
													{formatDateTime(stop.scheduled_delivery)}
												</span>
											</div>
										)}

										{isDelivered && stop.actual_arrival && (
											<div style={styles.stopDetail}>
												<span style={styles.stopLabel}>Actual Arrival:</span>
												<span style={{ ...styles.stopValue, color: '#16a34a', fontWeight: 500 }}>
													{formatDateTime(stop.actual_arrival)}
												</span>
											</div>
										)}
									</div>
								)
							})}
					</div>
				)}
			</div>
		</div>
	)
}

const styles: Record<string, React.CSSProperties> = {
	panel: {
		width: '360px',
		minWidth: '360px',
		backgroundColor: '#ffffff',
		borderLeft: '1px solid #e5e7eb',
		display: 'flex',
		flexDirection: 'column',
		overflow: 'hidden',
		boxShadow: '-4px 0 12px rgba(0, 0, 0, 0.1)',
		flexShrink: 0,
	},
	header: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		padding: '16px',
		borderBottom: '1px solid #e5e7eb',
		flexShrink: 0,
	},
	scrollableContent: {
		flex: 1,
		overflowY: 'auto',
		overflowX: 'hidden',
		minHeight: 0,
	},
	headerLeft: {
		display: 'flex',
		alignItems: 'center',
		gap: '10px',
	},
	proNumber: {
		fontSize: '18px',
		fontWeight: 700,
		color: '#111827',
	},
	statusBadge: {
		padding: '4px 10px',
		borderRadius: '4px',
		fontSize: '12px',
		fontWeight: 600,
	},
	closeButton: {
		width: '32px',
		height: '32px',
		border: 'none',
		background: 'transparent',
		fontSize: '18px',
		color: '#6b7280',
		cursor: 'pointer',
		borderRadius: '4px',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
	},
	section: {
		padding: '12px 16px',
		borderBottom: '1px solid #f3f4f6',
		flexShrink: 0,
	},
	sectionLabel: {
		fontSize: '11px',
		color: '#6b7280',
		textTransform: 'uppercase',
		letterSpacing: '0.5px',
		marginBottom: '4px',
	},
	sectionValue: {
		fontSize: '14px',
		fontWeight: 500,
		color: '#111827',
	},
	arrivalTime: {
		fontSize: '16px',
		fontWeight: 600,
		color: '#16a34a',
	},
	detailsGrid: {
		padding: '16px',
		display: 'grid',
		gap: '16px',
	},
	detailItem: {
		display: 'flex',
		flexDirection: 'column',
		gap: '4px',
	},
	detailLabel: {
		fontSize: '11px',
		color: '#6b7280',
		textTransform: 'uppercase',
		letterSpacing: '0.5px',
	},
	detailValue: {
		fontSize: '14px',
		fontWeight: 500,
		color: '#111827',
	},
	stopsSection: {
		padding: '0 16px 16px',
	},
	stopsHeader: {
		fontSize: '12px',
		fontWeight: 600,
		color: '#6b7280',
		textTransform: 'uppercase',
		letterSpacing: '0.5px',
		marginBottom: '12px',
		paddingTop: '8px',
		borderTop: '1px solid #e5e7eb',
	},
	stopCard: {
		backgroundColor: '#f9fafb',
		borderRadius: '8px',
		padding: '12px',
		marginBottom: '8px',
		border: '1px solid #e5e7eb',
	},
	stopHeader: {
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
		marginBottom: '8px',
	},
	stopSequence: {
		display: 'flex',
		alignItems: 'center',
		gap: '4px',
	},
	sequenceLabel: {
		fontSize: '12px',
		color: '#6b7280',
	},
	sequenceValue: {
		fontSize: '14px',
		fontWeight: 600,
		color: '#111827',
	},
	stopStatus: {
		padding: '2px 8px',
		borderRadius: '4px',
		fontSize: '11px',
		fontWeight: 600,
	},
	stopDetail: {
		display: 'flex',
		gap: '8px',
		marginBottom: '4px',
	},
	stopLabel: {
		fontSize: '12px',
		color: '#6b7280',
		minWidth: '80px',
	},
	stopValue: {
		fontSize: '12px',
		color: '#111827',
		flex: 1,
	},
}
